import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAgentLogsFilterUrlState } from './useAgentLogsFilterUrlState';
import {
  DEFAULT_AGENT_TIME_RANGE,
  INTERVAL_TO_API_MAP,
  URL_TO_TIME_MAP
} from '../constants';
import { mapAgentLogsFiltersToQueryParams } from '../mapAgentLogsFiltersToQueryParams';

import type { TimeRange } from '~/components/TimeRangeSelector/TimeRangeSelector';
import { useTimeRangeCalculation } from '~/scenes/Monitor/scenes/PipelineLogs/hooks/useTimeRangeCalculation';

export function useAgentLogsFilters() {
  const { filters, setters, clearAllFilters } = useAgentLogsFilterUrlState();
  const [resetToDefault, setResetToDefault] = useState(false);

  const [localPipelineName, setLocalPipelineName] = useState(
    filters.pipelineName
  );
  const [localAgentName, setLocalAgentName] = useState(filters.agentName);
  const [localContent, setLocalContent] = useState(filters.content);

  useEffect(() => {
    setLocalPipelineName(filters.pipelineName);
    setLocalAgentName(filters.agentName);
    setLocalContent(filters.content);
  }, [filters.pipelineName, filters.agentName, filters.content]);

  const {
    setPipelineName,
    setAgentName,
    setContent,
    setTime,
    setDateFrom,
    setDateTo
  } = setters;

  const { time, dateFrom, dateTo } = filters;

  const handleSearch = useCallback(() => {
    setPipelineName(localPipelineName);
    setAgentName(localAgentName);
    setContent(localContent);

    const internalTimeValue = URL_TO_TIME_MAP[time] || '5m';

    if (internalTimeValue === 'custom-range' && dateFrom && dateTo) {
      setDateFrom(dateFrom);
      setDateTo(dateTo);
    } else {
      const toDateValue = new Date();
      const fromDateValue = new Date();

      switch (internalTimeValue) {
        case '5m':
          fromDateValue.setMinutes(toDateValue.getMinutes() - 5);
          break;
        case '15m':
          fromDateValue.setMinutes(toDateValue.getMinutes() - 15);
          break;
        case '1h':
          fromDateValue.setHours(toDateValue.getHours() - 1);
          break;
        default:
          fromDateValue.setMinutes(toDateValue.getMinutes() - 5);
      }

      setDateFrom(fromDateValue.toISOString());
      setDateTo(toDateValue.toISOString());
    }
  }, [
    localPipelineName,
    localAgentName,
    localContent,
    time,
    dateFrom,
    dateTo,
    setPipelineName,
    setAgentName,
    setContent,
    setDateFrom,
    setDateTo
  ]);

  const handleTimeRangeChange = useCallback(
    (timeRange: TimeRange) => {
      setResetToDefault(false);

      if (timeRange.name === 'custom-range') {
        setTime(INTERVAL_TO_API_MAP['custom-range']);
        setDateFrom(timeRange.dateFrom);
        setDateTo(timeRange.dateTo);
      } else {
        setTime(
          INTERVAL_TO_API_MAP[
            timeRange.name as keyof typeof INTERVAL_TO_API_MAP
          ] || DEFAULT_AGENT_TIME_RANGE
        );
        setDateFrom(null);
        setDateTo(null);
      }
    },
    [setTime, setDateFrom, setDateTo]
  );

  const handleClear = useCallback(() => {
    setLocalPipelineName('');
    setLocalAgentName('');
    setLocalContent('');
    clearAllFilters();
    setResetToDefault(true);
  }, [clearAllFilters]);

  const timeRange = useTimeRangeCalculation({
    time,
    dateFrom,
    dateTo
  });

  const queryParams = useMemo(
    () =>
      mapAgentLogsFiltersToQueryParams({
        pipelineName: filters.pipelineName,
        agentName: filters.agentName,
        content: filters.content,
        time: timeRange.apiTime,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      }),
    [
      filters.pipelineName,
      filters.agentName,
      filters.content,
      filters.dateFrom,
      filters.dateTo,
      timeRange.apiTime
    ]
  );

  return {
    form: {
      values: {
        pipelineName: localPipelineName,
        agentName: localAgentName,
        content: localContent
      },
      setters: {
        setPipelineName: setLocalPipelineName,
        setAgentName: setLocalAgentName,
        setContent: setLocalContent
      }
    },
    urlParams: {
      pipelineName: filters.pipelineName || null,
      agentName: filters.agentName || null,
      content: filters.content || null,
      dateFrom,
      dateTo
    },
    timeState: {
      time,
      dateFrom,
      dateTo,
      resetToDefault
    },
    timeRange: timeRange.currentTimeRange,
    queryParams,
    handleSearch,
    handleTimeRangeChange,
    handleClear
  };
}
